import { buildNormalizedFields } from './apartmentKey';

// Pure decision logic for editing a member in the member list (Medlemsförteckning).
//
// Background: a `housing` document represents ONE apartment. `apartmentNumber`
// lives at the document level, while `members[]` are the co-owners that share
// that apartment number. The edit modal historically wrote `apartmentNumber`
// straight to the document, so changing the number for one member in a
// multi-member document also changed it for every other member in the same
// document (they all read the shared document-level field).
//
// Fix: if the apartment number is changed for a member that lives in a
// multi-member document, that member is SPLIT out into its own housing
// document with the new number, and removed from the original document. The
// remaining members keep their (unchanged) apartment number. Editing only
// name/contact details, or editing a single-member document, updates in place
// and keeps genuine co-owners grouped together.

export function buildMemberFromForm(formData) {
  return {
    firstName: (formData.firstName || '').trim(),
    lastName: (formData.lastName || '').trim(),
    legalEntity: (formData.legalEntity || '').trim(),
    email: (formData.email || '').trim(),
    phone: (formData.phone || '').trim(),
  };
}

// Delad med importen och ansökningsflödet (utils/apartmentKey). Den här filen
// hade tidigare en egen, svagare variant: bara lowercase och trim, vilket gav
// nycklar som aldrig matchade importens. Re-exporteras för befintliga anropare.
export { buildNormalizedFields };

/**
 * Decide how to persist an edit to a member.
 *
 * @param {object} params
 * @param {object} params.docData       Current housing document data (fresh read).
 * @param {number} params.memberIndex   Index of the member being edited.
 * @param {object} params.formData      Edited form values.
 * @returns {{action: 'split'|'update', ...}}
 */
export function planMemberEdit({ docData, memberIndex, formData }) {
  const existingMembers = (docData && docData.members) || [];
  const idx = memberIndex ?? 0;
  const editedMember = buildMemberFromForm(formData);
  const normalizedFields = buildNormalizedFields(formData);

  /**
   * En lägenhet identifieras av adress + lägenhetsnummer + postnummer. Ändras
   * någon av dem flyttar medlemmen till en annan lägenhet och måste brytas ut
   * till ett eget dokument — annars skrivs dokumentnivåfälten om för ALLA
   * medlemmar i dokumentet, och grannarna får den redigerades adress.
   *
   * Tidigare tittade villkoret bara på lägenhetsnumret. Skatteverkets
   * lägenhetsnummer är inte unikt (1301 återkommer i varje uppgång), så
   * grannar i olika uppgångar hamnar i samma dokument — och just en
   * adressändring smittade dem då.
   */
  const currentIdentity = buildNormalizedFields(docData || {});

  // Tomt fält som fylls i är en komplettering, inte en flytt. Undantaget är
  // lägenhetsnumret: att sätta ett nummer på en medlem i ett delat dokument är
  // just det som bryter ut hen.
  const filledInOrChanged = (before, after) => before !== after;
  const changedBetweenValues = (before, after) =>
    Boolean(before) && Boolean(after) && before !== after;

  const identityChanged =
    filledInOrChanged(
      currentIdentity.normalizedApartmentNumber,
      normalizedFields.normalizedApartmentNumber
    ) ||
    changedBetweenValues(
      currentIdentity.normalizedAddress,
      normalizedFields.normalizedAddress
    ) ||
    changedBetweenValues(
      currentIdentity.normalizedZipCode,
      normalizedFields.normalizedZipCode
    );

  const isMultiMember = existingMembers.length > 1;

  if (identityChanged && isMultiMember) {
    const remainingMembers = existingMembers.filter((_, i) => i !== idx);
    return {
      action: 'split',
      // New standalone housing doc for the edited member at the new apartment number.
      newDoc: {
        apartmentNumber: formData.apartmentNumber,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        members: [editedMember],
        ...normalizedFields,
        associationId: docData.associationId,
        hasMemberInfo: true,
        ownedBy: null,
      },
      // Original doc: drop the edited member, KEEP its apartment number untouched.
      originalUpdate: {
        members: remainingMembers,
        hasMemberInfo: remainingMembers.length > 0,
      },
    };
  }

  const updatedMembers = [...existingMembers];
  updatedMembers[idx] = editedMember;
  return {
    action: 'update',
    update: {
      apartmentNumber: formData.apartmentNumber,
      address: formData.address,
      zipCode: formData.zipCode,
      city: formData.city,
      members: updatedMembers,
      ...normalizedFields,
      hasMemberInfo: true,
    },
  };
}
